import React, { useState } from "react";
import { Aurora, Card, PageHeader, Logo, Stepper } from "./Shell";
import Instructions from "./Instructions";
import { useDemo, useDemoStep } from "./DemoContext";

const TECHNIQUES = {
  A: [
    { id: "tongue", label: "Tongue to roof of mouth" },
    { id: "humming", label: "Humming / vocalization" },
    { id: "exhalation", label: "Extended exhalation" },
    { id: "audio", label: "Soothing background audio" },
  ],
  B: [
    { id: "butterfly_hug", label: "Butterfly hug" },
    { id: "tongue", label: "Tongue to roof of mouth" },
    { id: "humming", label: "Humming / vocalization" },
    { id: "exhalation", label: "Extended exhalation" },
    { id: "audio", label: "Soothing background audio" },
  ],
};

// Static "how to choose" guidance + the bullets for each mode card —
// hoisted so we don't allocate fresh arrays on every render.
const CHOOSE_INSTRUCTIONS = [
  { strong: "Mode A (Tap-Based)", text: "is best when you're restless, wound-up, or your mind is racing. Keeping your hands busy gives that anxious energy somewhere to go." },
  { strong: "Mode B (Hands-Free)", text: "is best when you can be still and want to pair the session with the butterfly hug, slow breathing, or body-focused practice." },
  { strong: "Not sure?", text: "When in doubt, pick Mode A first. You can run Mode B next time and compare which feels better in your body." },
  { strong: "Layered techniques are optional", text: "but the long exhale and humming are the highest-leverage add-ons — they directly engage the vagal brake." },
];

const MODE_A_BULLETS = [
  "Tap large LEFT / RIGHT targets at your own pace",
  "Built-in engagement via timestamped tap log",
  "Best for: restless, anxious, wound-up states",
  "Not compatible with butterfly hug (hands busy)",
];

const MODE_B_BULLETS = [
  "Smooth dot traverses the screen on auto",
  "Adjust cadence (0.4–1.6 Hz), speed, duration",
  "Best for: butterfly hug, body-focused practice",
  "Engagement signal: session completion",
];

export default function ModeSelection({ onSelect, stepIndex, steps }) {
  const [hover, setHover] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [techniques, setTechniques] = useState([]);

  const toggleTech = (id) =>
    setTechniques((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const confirm = () => {
    if (!chosen) return;
    onSelect({ mode: chosen, techniques });
  };

  // Demo auto-fill: pick Mode A + one technique, then continue.
  const demo = useDemo();
  useDemoStep(() => {
    setChosen("A");
    setTechniques(["exhalation"]);
    setTimeout(() => {
      if (demo.active) onSelect({ mode: "A", techniques: ["exhalation"] });
    }, 1600);
  }, 1500);

  return (
    <Aurora>
      <div className="relative max-w-6xl mx-auto px-6 sm:px-10 py-10">
        <div className="flex items-center justify-between mb-12">
          <Logo size="md" />
          <Stepper steps={steps} current={stepIndex} />
        </div>

        <PageHeader
          eyebrow="Step 03 · Choose Your Mode"
          title="Active or receptive?"
          subtitle="Both modes deliver the same bilateral effect. Choose the one that fits how you feel right now."
        />

        <Instructions title="How to choose" items={CHOOSE_INSTRUCTIONS} />

        <div className="grid lg:grid-cols-2 gap-6 fade-up">
          <ModeCard
            id="A"
            title="Tap-Based"
            tag="Active engagement"
            chosen={chosen === "A"}
            onClick={() => setChosen("A")}
            onMouseEnter={() => setHover("A")}
            onMouseLeave={() => setHover(null)}
            visual={
              <div className="flex items-center justify-between px-2">
                <div className="w-14 h-14 rounded-2xl bg-[color:var(--accent-cyan-muted)] border border-[color:var(--accent-cyan)] pulse-glow" />
                <div className="font-mono text-2xl text-cyan-glow">L ↔ R</div>
                <div className="w-14 h-14 rounded-2xl bg-[color:var(--accent-cyan-muted)] border border-[color:var(--accent-cyan)] pulse-glow" style={{ animationDelay: "0.7s" }} />
              </div>
            }
            bullets={MODE_A_BULLETS}
          />

          <ModeCard
            id="B"
            title="Hands-Free"
            tag="Receptive watching"
            chosen={chosen === "B"}
            onClick={() => setChosen("B")}
            onMouseEnter={() => setHover("B")}
            onMouseLeave={() => setHover(null)}
            visual={
              <div className="relative h-14 mx-2 rounded-2xl bg-[color:var(--bg-soft)] border border-[color:var(--hairline)] overflow-hidden">
                <div
                  className="bs-dot absolute top-1/2 -translate-y-1/2"
                  style={{ left: "8%", animation: "modeB-preview 2.6s ease-in-out infinite alternate" }}
                />
                <style>{`@keyframes modeB-preview { from { transform: translate(-50%, -50%); left: 8%; } to { left: 92%; } }`}</style>
              </div>
            }
            bullets={MODE_B_BULLETS}
          />
        </div>

        {chosen && (
          <div className="mt-12 fade-up">
            <Card>
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--accent-cyan)] mb-3">
                Step 04 · Layered Somatic Techniques (optional)
              </div>
              <div className="text-ink text-xl font-light mb-5">
                Stack one or more practices with your bilateral session.
              </div>
              <div className="flex flex-wrap gap-3" data-testid="techniques-grid">
                {TECHNIQUES[chosen].map((t) => {
                  const on = techniques.includes(t.id);
                  return (
                    <button
                      type="button"
                      key={t.id}
                      onClick={() => toggleTech(t.id)}
                      className={`px-5 py-3 rounded-full text-sm border transition-all ${
                        on
                          ? "bg-[color:var(--accent-cyan-muted)] border-[color:var(--accent-cyan)] text-[color:var(--accent-cyan)]"
                          : "border-[color:var(--hairline)] text-[color:var(--text-secondary)] hover:border-[color:rgba(15,26,46,0.20)] hover:text-white"
                      }`}
                      data-testid={`technique-${t.id}-btn`}
                    >
                      {on ? "● " : "○ "}
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </Card>
          </div>
        )}

        <div className="mt-10 flex items-center gap-4">
          <button
            type="button"
            className="btn-primary"
            disabled={!chosen}
            onClick={confirm}
            data-testid="mode-confirm-btn"
          >
            {chosen ? `Begin Mode ${chosen} →` : "Select a mode"}
          </button>
        </div>
      </div>
    </Aurora>
  );
}

function ModeCard({ id, title, tag, chosen, visual, bullets, onClick, ...rest }) {
  return (
    <button
      type="button"
      onClick={onClick}
      {...rest}
      data-testid={`mode-${id}-card`}
      className={`text-left rounded-2xl p-8 transition-all border ${
        chosen
          ? "glass-strong border-[color:var(--accent-cyan)] shadow-[0_0_60px_rgba(0,191,255,0.25)]"
          : "glass border-[color:var(--hairline)] hover:border-[color:rgba(15,26,46,0.20)]"
      }`}
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--accent-cyan)] mb-2">
            Mode {id}
          </div>
          <div className="text-3xl text-ink font-light">{title}</div>
          <div className="text-sm text-[color:var(--text-secondary)] mt-1">{tag}</div>
        </div>
        <div
          className={`w-5 h-5 rounded-full border-2 ${
            chosen
              ? "border-[color:var(--accent-cyan)] bg-[color:var(--accent-cyan)]"
              : "border-[color:rgba(15,26,46,0.20)]"
          }`}
        />
      </div>
      <div className="my-6">{visual}</div>
      <ul className="space-y-2 mt-4">
        {bullets.map((b) => (
          <li
            key={b}
            className="text-sm text-[color:var(--text-secondary)] flex gap-2 leading-relaxed"
          >
            <span className="text-[color:var(--accent-cyan)] font-mono">›</span>
            {b}
          </li>
        ))}
      </ul>
    </button>
  );
}
